// @ts-check

/**
 * @typedef {Object} ProrationInput
 * @property {number} unusedCredits - Included credits minus credits consumed this cycle (>= 0)
 * @property {number} pricePerCredit - $/credit rate from PREPAID_CREDIT_PACKS.core
 * @property {number} currentTierPrice - Full cycle price of the current tier
 * @property {number} includedCredits - Per-cycle included credits (monthly or annual)
 * @property {number} addOnCost - Per-cycle add-on costs (e.g. seats)
 * @property {number} cycleStartSeconds - Billing cycle start (Unix timestamp in seconds)
 * @property {number} cycleEndSeconds - Billing cycle end (Unix timestamp in seconds)
 */

/**
 * @typedef {Object} ProrationResult
 * @property {number} unusedCreditRefund - Refund for unused credits
 * @property {number} serviceFee - Base service fee (tier price minus credit costs plus add-ons)
 * @property {number} serviceFeeRefund - Prorated refund of the service fee for remaining days
 * @property {number} timeRemainingRatio - Fraction of the billing cycle still remaining
 * @property {number} totalDeduction - Total deduction in dollars (unusedCreditRefund + serviceFeeRefund)
 * @property {number} daysRemaining - Whole days remaining in the cycle (excluding today)
 * @property {number} totalCycleDays - Total days in the billing cycle
 */

const SECONDS_PER_DAY = 86400;

/**
 * Compute proration values for a Core plan tier change.
 * Pure math -- no side effects, no data fetching.
 *
 * Formula:
 *   totalDeduction = unusedCreditRefund + serviceFeeRefund
 *   unusedCreditRefund = unusedCredits x pricePerCredit
 *   serviceFeeRefund = serviceFee x (daysRemaining / totalCycleDays)
 *   serviceFee = currentTierPrice - (includedCredits x pricePerCredit) + addOnCost
 *
 * Time is measured in whole days, excluding today. See PRORATE_DISCOUNTS.md for full docs.
 *
 * @param {ProrationInput} params
 * @returns {ProrationResult}
 */
function computeProration({
    unusedCredits,
    pricePerCredit,
    currentTierPrice,
    includedCredits,
    addOnCost,
    cycleStartSeconds,
    cycleEndSeconds
}) {
    // Time remaining in whole days, excluding today
    const totalCycleDays = Math.floor((cycleEndSeconds - cycleStartSeconds) / SECONDS_PER_DAY);
    const startOfTomorrowSeconds = Math.ceil(Date.now() / 1000 / SECONDS_PER_DAY) * SECONDS_PER_DAY;
    const daysRemaining = Math.max(
        0,
        Math.floor((cycleEndSeconds - startOfTomorrowSeconds) / SECONDS_PER_DAY)
    );
    const timeRemainingRatio = totalCycleDays > 0 ? daysRemaining / totalCycleDays : 0;

    // Credit refund: all unused credits at the per-credit rate
    const unusedCreditRefund = unusedCredits * pricePerCredit;

    // Service fee: tier price minus the credit portion, plus add-on costs (seats etc.)
    const creditsCost = includedCredits * pricePerCredit;
    const serviceFee = Math.max(0, currentTierPrice - creditsCost) + addOnCost;

    // Prorated service fee refund for the remaining days
    const serviceFeeRefund = serviceFee * timeRemainingRatio;

    // Total deduction
    const totalDeduction = unusedCreditRefund + serviceFeeRefund;

    return {
        unusedCreditRefund,
        serviceFee,
        serviceFeeRefund,
        timeRemainingRatio,
        totalDeduction,
        daysRemaining,
        totalCycleDays
    };
}

module.exports = { computeProration };
